import { z } from 'zod';

import { RECORD_SORTS, listQuerySchema, sortParam } from './pagination';

/*
 * Conversations, shared between the server and the screens — ROADMAP.md §9.3
 * onward, business-rules.md §10, data-model.md §8a.
 *
 * **The labels live here because CLAUDE.md §4 forbids rendering a raw enum.**
 * `waiting_customer` must never reach a person's eyes, and one map used by both
 * sides is what stops the inbox and the thread disagreeing about what a status
 * is called.
 */

export const CONVERSATION_STATUS_VALUES = [
  'open',
  'waiting_customer',
  'waiting_us',
  'closed',
] as const;
export type ConversationStatusValue = (typeof CONVERSATION_STATUS_VALUES)[number];

export const CONVERSATION_STATUS_LABELS: Readonly<Record<ConversationStatusValue, string>> = {
  open: 'Open',
  waiting_customer: 'Waiting on customer',
  waiting_us: 'Waiting on us',
  closed: 'Closed',
};

/**
 * The kind of entry, which is what this column has always really been —
 * `note` has been on it since 019. Migration 030 added `phone`.
 */
export const MESSAGE_DIRECTION_VALUES = ['inbound', 'outbound', 'note', 'phone'] as const;
export type MessageDirectionValue = (typeof MESSAGE_DIRECTION_VALUES)[number];

/** The kinds that are internal to the office and are never sent to anybody. */
export const INTERNAL_DIRECTION_VALUES: readonly MessageDirectionValue[] = ['note', 'phone'];

export const DELIVERY_STATUS_VALUES = [
  'pending',
  'sent',
  'delivered',
  'failed',
  'bounced',
] as const;
export type DeliveryStatusValue = (typeof DELIVERY_STATUS_VALUES)[number];

/**
 * What the office is told about a message's fate.
 *
 * **There is no "read", deliberately** — owner decision, no tracking pixel
 * (business-rules.md §10). `delivered` and `failed` are real: they come from the
 * SMTP result and from the bounce lane. A "read" count would under-report every
 * time a mail client proxies images, and would be a number people trusted.
 */
export const DELIVERY_STATUS_LABELS: Readonly<Record<DeliveryStatusValue, string>> = {
  pending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Not delivered',
  bounced: 'Bounced back',
};

export const MATCH_METHOD_VALUES = ['token', 'headers', 'manual', 'form'] as const;
export type MatchMethodValue = (typeof MATCH_METHOD_VALUES)[number];

/**
 * How sure we are that a message came from who it says.
 *
 * **Shown in the thread, not hidden as metadata**, because it is the difference
 * between a reply we can trust and one somebody typed into a triage queue. A
 * message matched by `token` came back down an address only that customer was
 * given. `manual` was filed by a person and says nothing about the sender.
 * `form` came through the website, where anyone can type any address.
 */
export const MATCH_METHOD_LABELS: Readonly<Record<MatchMethodValue, string>> = {
  token: 'Verified reply',
  headers: 'Matched by mail headers',
  manual: 'Filed by hand',
  form: 'Sent through the website',
};

export interface ConversationAttachmentView {
  id: string;
  fileName: string;
  /** As detected from the bytes — never what the sender declared. */
  contentType: string;
  sizeBytes: number;
  /** Served as a download; nothing is ever opened in place. */
  downloadPath: string;
}

export interface ConversationMessageView {
  id: string;
  direction: MessageDirectionValue;

  /** The admin who wrote it, for outbound and notes. Null for inbound. */
  authorName: string | null;

  fromAddress: string;
  fromName: string | null;
  toAddresses: string | null;
  ccAddresses: string | null;

  /** Shown only where it changes, so a thread is not a column of "Re: …". */
  subject: string | null;

  bodyText: string | null;

  /** Already sanitised on write. Still rendered in a sandboxed frame. */
  bodyHtml: string | null;

  /** True when the HTML carries images we neutralised — offers "show images". */
  hasBlockedImages: boolean;

  attachments: ConversationAttachmentView[];

  deliveryStatus: DeliveryStatusValue;
  deliveryDetail: string | null;
  matchMethod: MatchMethodValue | null;

  /** Ours on outbound, theirs on inbound. Shown in the details panel. */
  messageIdHeader: string | null;
  inReplyTo: string | null;

  /**
   * Whether the original is still stored, so the details panel can offer the
   * raw headers rather than a link that leads to an error.
   *
   * **A boolean, never the object key** — a storage key is not something a
   * browser should ever hold (CLAUDE.md §4).
   */
  hasSource: boolean;

  createdAt: string;
}

export interface ConversationEventView {
  id: string;
  kind: string;
  /** Already turned into a sentence server-side — never a raw enum. */
  description: string;
  actorName: string | null;
  createdAt: string;
}

export interface ConversationDetail {
  id: string;
  reference: string;
  subject: string;
  status: ConversationStatusValue;
  statusLabel: string;

  contactEmail: string;
  contactName: string | null;

  clientId: string | null;
  clientName: string | null;
  leadId: string | null;

  /** Hiring or not — see `ConversationKind`. */
  kind: ConversationKind;
  kindLabel: string;
  jobApplicationId: string | null;
  applicantName: string | null;

  assignedToUserId: string | null;
  assignedToName: string | null;

  isSpam: boolean;
  /**
   * True when attached to nobody at all — the triage queue. **An applicant
   * thread is attached to somebody**, so it is never triage.
   */
  isTriage: boolean;

  lastMessageAt: string;
  createdAt: string;

  messages: ConversationMessageView[];
  events: ConversationEventView[];
}

export const conversationStatusSchema = z.object({
  status: z.enum(CONVERSATION_STATUS_VALUES),
});
export type ConversationStatusInput = z.infer<typeof conversationStatusSchema>;

export const conversationAssignmentSchema = z.object({
  /** Null unassigns. Assignment is not access control — it says whose job it is. */
  assignedToUserId: z.string().uuid().nullable(),
});
export type ConversationAssignmentInput = z.infer<typeof conversationAssignmentSchema>;

export const conversationNoteSchema = z.object({
  /**
   * **An internal note. Never sent, to anybody.** It has no recipient field and
   * cannot grow one here — the endpoint behind this writes `direction = 'note'`
   * and the database refuses to record a note as delivered.
   */
  bodyText: z.string().trim().min(1, 'Write something first.').max(20_000),
});
export type ConversationNoteInput = z.infer<typeof conversationNoteSchema>;

/**
 * A phone call, written down after the fact.
 *
 * **The same shape as a note and the same guarantees** — no recipient, never
 * sent, `delivery_status` pinned to `pending` by the database. What makes it
 * different is that it counts as contact: a note is the office talking to
 * itself, a call is the office talking to the customer, and `lastContact` on
 * the client record tells them apart.
 */
export const conversationPhoneLogSchema = z.object({
  bodyText: z.string().trim().min(1, 'Say what the call was about.').max(20_000),
});
export type ConversationPhoneLogInput = z.infer<typeof conversationPhoneLogSchema>;

export const conversationReplySchema = z.object({
  /**
   * **What gets sent to the customer.** Plain text — see the note in
   * `conversation-send-service.ts` on why there is no rich-text editor yet.
   */
  bodyText: z.string().trim().min(1, 'Write a message first.').max(100_000),

  /**
   * Optional. Defaults to the thread's own subject with one `Re:`.
   *
   * **There is no recipient field here, deliberately.** The address comes from
   * the conversation, so a reply cannot be aimed at somebody who is not part of
   * the thread — and now that mail genuinely leaves the building, a
   * recipient-shaped hole in this schema would be the way a message reaches the
   * wrong person.
   */
  subject: z.string().trim().min(1).max(255).optional(),
});
export type ConversationReplyInput = z.infer<typeof conversationReplySchema>;

/**
 * Start a conversation with somebody — owner instruction, 2026-08-07.
 *
 * His finding was that there was no way for an admin to open one: *"There is no
 * way for an admin to start one. No button, anywhere."* A thread could only
 * come from an invoice, an inbound email, or the back-fill CLI.
 *
 * **The recipient is a customer or an enquiry, by id — never a typed address.**
 * That is the same rule as replying, and for the same reason: the address comes
 * from the record, so a message cannot be aimed at somebody who is not on it.
 * It also means this cannot be used to send mail to a stranger, which is a
 * thing the office has not asked for and an open relay is a thing nobody wants.
 */
export const startConversationSchema = z
  .object({
    clientId: z.uuid().optional(),
    leadId: z.uuid().optional(),

    /**
     * **Written by the person starting it**, with no `Re:` and no default.
     * A thread the office opens is about something, and a subject line reading
     * "Message from Honeycomb" is one nobody can find again in a search.
     */
    subject: z.string().trim().min(1, 'What is this about?').max(255),

    bodyText: z.string().trim().min(1, 'Write a message first.').max(100_000),
  })
  .refine((value) => (value.clientId === undefined) !== (value.leadId === undefined), {
    // Exactly one. Both would be ambiguous about whose record it belongs to;
    // neither would be a message to nobody.
    message: 'Start a conversation from a customer or an enquiry.',
    path: ['clientId'],
  });
export type StartConversationInput = z.infer<typeof startConversationSchema>;

export interface StartConversationResult {
  conversationId: string;
  /** What actually happened to the first message. `failed` still leaves it on the thread. */
  status: 'sent' | 'failed' | 'suppressed';
  error?: string;
}

/**
 * The inbox filters — ROADMAP.md §9.5.
 *
 * `triage` is not a status: it is "attached to neither a client nor a lead",
 * which is a different question and the one the triage queue asks. Keeping it
 * out of the status enum is what stops a thread being both `open` and `triage`
 * in a way nothing can represent.
 */
export const CONVERSATION_INBOX_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'waiting_customer', label: 'Waiting on customer' },
  { value: 'waiting_us', label: 'Waiting on us' },
  { value: 'closed', label: 'Closed' },
  { value: 'triage', label: 'Needs filing' },
  { value: 'applicants', label: 'Job applicants' },
  { value: 'spam', label: 'Spam' },
] as const;

/**
 * Who a thread is with — owner decision, 2026-08-07.
 *
 * **Two kinds, and the difference is not cosmetic.** A hiring thread is
 * readable by super admins only, and the owner asked for it to be *"clear and
 * obvious that they are not customers"*. Derived server-side from
 * `conversation.job_application_id` rather than stored, so it cannot disagree
 * with the column the visibility rule is enforced on.
 */
export const CONVERSATION_KINDS = ['customer', 'applicant'] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

/** Never render the value — CLAUDE.md §4. */
export const CONVERSATION_KIND_LABELS: Readonly<Record<ConversationKind, string>> = {
  customer: 'Customer',
  applicant: 'Job applicant',
};

export const conversationListQuerySchema = listQuerySchema.extend({
  sort: sortParam(RECORD_SORTS, 'newest'),

  status: z
    .enum([...CONVERSATION_STATUS_VALUES, 'all', 'triage', 'applicants', 'spam'])
    .default('all'),
  /** `"me"` is resolved server-side against the signed-in admin. */
  assignedTo: z.union([z.literal('me'), z.literal('unassigned'), z.uuid()]).optional(),
  /** ISO dates, inclusive. */
  from: z.string().date().optional(),
  to: z.string().date().optional(),

  /**
   * Threads on one customer or one enquiry — 9.7's correspondence panes.
   *
   * Filters on the same endpoint rather than two new ones, so the leads pane,
   * the client record and the inbox all read conversations through one query
   * with one set of rules about spam and ordering.
   */
  clientId: z.uuid().optional(),
  leadId: z.uuid().optional(),
});
export type ConversationListQuery = z.infer<typeof conversationListQuerySchema>;

export interface ConversationListItem {
  id: string;
  reference: string;
  subject: string;
  status: ConversationStatusValue;
  statusLabel: string;
  contactEmail: string;
  contactName: string | null;
  clientId: string | null;
  clientName: string | null;
  assignedToName: string | null;
  unreadCount: number;
  isSpam: boolean;
  /** Hiring or not. The inbox row carries a badge when it is not a customer. */
  kind: ConversationKind;
  kindLabel: string;
  isTriage: boolean;
  /** The first line of the most recent message, for the list row. */
  preview: string | null;
  lastMessageAt: string;
}

export interface ConversationAssignee {
  id: string;
  name: string;
}
